import React, { useEffect, useRef } from 'react';

const MIDDLE_MOUSE_BUTTON = 1;
const LEFT_MOUSE_BUTTON = 0;

export type TooltipProps = {
  sticky?: boolean;
  position?: { x: number; y: number };
  visible?: boolean;
  style?: React.CSSProperties;
  className?: string;
  maxWidth?: number;
  children: React.ReactNode | React.ReactNode[];
  container?: HTMLElement;
};

export const Tooltip: React.FC<TooltipProps> = ({
  sticky = true,
  position,
  visible = false,
  children,
  style,
  className,
  container = document.body,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect((): (() => void) | void => {
    const posPadding = { x: 16, y: 45 };
    if (sticky) {
      const listener = (e: MouseEvent) => {
        if (!tooltipRef.current) return;
        // check if mouse is inside the area of the container
        if (
          e.clientX < container.offsetLeft ||
          e.clientX > container.offsetLeft + container.offsetWidth + 20 ||
          e.clientY < container.offsetTop ||
          e.clientY > container.offsetTop + container.offsetHeight + 20
        ) {
          return;
        }
        const mousePos = { x: e.clientX, y: e.clientY };
        tooltipRef.current.style.left = `${mousePos.x + posPadding.x}px`;
        tooltipRef.current.style.right = 'auto';

        // If the mouse is too close to the right border of the screen, move the tooltip to the left
        if (
          mousePos.x + tooltipRef.current.offsetWidth >
          window.innerWidth - 20
        ) {
          tooltipRef.current.style.right = `${
            window.innerWidth - (mousePos.x - posPadding.x)
          }px`;
          tooltipRef.current.style.left = 'auto';
          tooltipRef.current.style.flexDirection = 'row-reverse';
        }

        tooltipRef.current.style.top = `${Math.min(
          window.innerHeight - tooltipRef.current.offsetHeight,
          mousePos.y - posPadding.y,
        )}px`;
      };

      const forceClose = (e: MouseEvent) => {
        if (
          !tooltipRef.current ||
          tooltipRef.current.style.display !== 'flex' ||
          (e.button !== MIDDLE_MOUSE_BUTTON && e.button !== LEFT_MOUSE_BUTTON)
        )
          return;
        if (e.button === MIDDLE_MOUSE_BUTTON) {
          e.preventDefault();
        }
        tooltipRef.current.style.display = 'none';
      };

      container.addEventListener('mousemove', listener);
      window.addEventListener('mousedown', forceClose);
      return () => {
        container.removeEventListener('mousemove', listener);
        window.removeEventListener('mousedown', forceClose);
      };
    } else {
      if (!position) {
        console.error('MapTooltip: position is required when sticky is false');
        return;
      }
      if (!tooltipRef.current) return;
      tooltipRef.current.style.left = `${position.x + posPadding.x}px`;
      tooltipRef.current.style.top = `${position.y - posPadding.y}px`;
    }
  }, [tooltipRef, sticky, position, container]);

  return (
    <div
      ref={tooltipRef}
      className={className}
      style={{
        display: visible ? 'flex' : 'none',
        position: 'absolute',
        zIndex: 1000,
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
    </div>
  );
};
